import { Editor, Node, Path, PathRef, Element as SlateElement, Transforms } from 'slate'

import {
  BLOCK_FORMATS,
  CustomEditor,
} from 'core-components/library/richTextEditor/interface/custom-types'

import { TCustomElement } from './../../interface/custom-types'
import { isList, isListItem } from './isListHelper'

type NodeRefEntry<T> = [T, PathRef]

export const getHighestSelectedElements = (editor: CustomEditor) => {
  const selection = editor.selection

  if (!selection) {
    return []
  }

  if (Path.equals(selection.anchor.path, selection.focus.path)) {
    const ancestor = Editor.above(editor, {
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    })

    return ancestor ? [ancestor] : []
  }

  const ancestorPath = Path.common(selection.anchor.path, selection.focus.path)

  const startIndex = Path.relative(selection.anchor.path, ancestorPath)[0]
  const endIndex = Path.relative(selection.focus.path, ancestorPath)[0]

  return [...Node.children(editor, ancestorPath)].slice(
    Math.min(startIndex, endIndex),
    Math.max(startIndex + 1, endIndex + 1)
  )
}

export const unwrapCurrentElements = (editor: CustomEditor) => {
  if (!editor.selection) {
    return
  }

  const { anchor, focus } = editor.selection

  const startNodePath = Path.isBefore(anchor.path, focus.path) ? anchor.path : focus.path
  const endNodePath = Path.isAfter(anchor.path, focus.path) ? anchor.path : focus.path

  const startNodeListItem = Editor.above(editor, { at: startNodePath, match: isListItem })
  const endNodeListItem = Editor.above(editor, { at: endNodePath, match: isListItem })
  if (startNodeListItem && endNodeListItem) {
    const firstSelectedElementPath = startNodeListItem[1]
    const lastSelectedElementPath = endNodeListItem[1]

    // Split the list at all nesting levels to separate the end of the unwrap part from the rest of the list
    let currentPath: Path | undefined = lastSelectedElementPath
    while (Node.has(editor, Path.next(currentPath))) {
      const currentNode = Node.get(editor, currentPath)

      if (SlateElement.isElement(currentNode) && (isList(currentNode) || isListItem(currentNode))) {
        Transforms.splitNodes(editor, { at: Path.next(currentPath) })
        currentPath = Path.parent(currentPath)
        const isNewCurrentNode =
          Node.has(editor, currentPath) && SlateElement.isElement(Node.get(editor, currentPath))
        if (!isNewCurrentNode) {
          break
        }
      } else {
        break
      }
    }

    // If this is the first element of the list, then we do not need to split the current node,
    // but we need to split the list closest to the top
    currentPath = firstSelectedElementPath

    while (!Path.hasPrevious(currentPath)) {
      const currentPathNode = Node.get(editor, currentPath)

      if (!SlateElement.isElement(currentPathNode)) {
        currentPath = undefined
        break
      }

      currentPath = Path.parent(currentPath)
    }

    // Split the list at all nesting levels to separate the beginning of the part allocated
    // for unwrap from the rest of the list
    while (currentPath && Node.has(editor, currentPath)) {
      const currentNode = Node.get(editor, currentPath)

      if (SlateElement.isElement(currentNode) && (isList(currentNode) || isListItem(currentNode))) {
        Transforms.splitNodes(editor, { at: currentPath })

        const parentPath = Path.parent(currentPath)
        if (SlateElement.isElement(Node.get(editor, parentPath))) {
          currentPath = Path.next(parentPath)
        } else {
          break
        }
      } else {
        break
      }
    }
  }

  Transforms.unwrapNodes(editor, { at: editor.selection, match: isList, mode: 'all' })
  Transforms.unwrapNodes(editor, { at: editor.selection, match: isListItem, mode: 'all' })

  return
}

/**
 * Wrap the blocks in the current selection in a new list.
 */
export const wrapInList = (editor: CustomEditor, format: BLOCK_FORMATS): void => {
  Editor.withoutNormalizing(editor, () => {
    if (!editor.selection) {
      return
    }

    const selectedElements = getHighestSelectedElements(editor)

    const [...lists] = Editor.nodes(editor, {
      match: isList,
      at: editor.selection,
      mode: 'lowest',
    })
    const listsPaths: Path[] = []
    let listsTypes: BLOCK_FORMATS | null = null

    if (lists.length) {
      lists.forEach(([list, path]) => {
        if ((list as TCustomElement).type !== (lists[0][0] as TCustomElement).type) {
          listsTypes = null
        } else {
          listsTypes = (lists[0][0] as TCustomElement).type
        }
        listsPaths.push(path)
      })
    }

    if (listsTypes === format) {
      unwrapCurrentElements(editor)

      return
    } else if (listsTypes && listsTypes !== format) {
      listsPaths.forEach((path) => {
        Transforms.setNodes(editor, { type: format }, { at: path })
      })

      return
    }

    const convertedSelectedElements: Array<NodeRefEntry<Node>> = selectedElements.map(
      ([node, path]) => [node, Editor.pathRef(editor, path)]
    )
    const newList = {
      type: format,
      children: [],
    }

    Transforms.wrapNodes(editor, newList, {
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    })

    // Wrap in list items
    convertedSelectedElements.forEach(([, pathRef]) => {
      if (pathRef.current && Node.has(editor, pathRef.current)) {
        const nodeForWrap = Node.get(editor, pathRef.current)
        if (nodeForWrap.hasOwnProperty('align')) {
          Transforms.unsetNodes(editor, 'align', { at: pathRef.current })
        }
      }
      Transforms.wrapNodes(
        editor,
        { type: BLOCK_FORMATS.listItem, children: [] },
        { at: pathRef.current || undefined }
      )

      pathRef.unref()
    })

    convertedSelectedElements.forEach(([node, pathRef]) => [node, pathRef.unref()])
  })
}
